import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { ArrowDown, ArrowLeft, ArrowRight, Plus, ShoppingBag, Trash2, CheckCircle2, Phone } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useBooking } from "@/store/booking";
import { listPublicProducts } from "@/lib/public.functions";
import { useProductsHidden } from "@/hooks/useSiteSettings";
import { submitRichiesta } from "@/lib/richieste.functions";
import type { Product } from "@/data/products";

export const Route = createFileRoute("/richieste")({
  head: () => ({
    meta: [
      { title: "Le tue richieste · La Bottega delle Carni da Marco" },
      { name: "description", content: "Componi la tua richiesta di disponibilità: aggiungi i prodotti al carrello, indica la data desiderata e ti ricontattiamo per confermare." },
      { property: "og:title", content: "Richiedi disponibilità · La Bottega delle Carni da Marco" },
      { property: "og:description", content: "Prepara la tua richiesta di disponibilità in bottega." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: RichiestePage,
});

function tomorrowIso() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 1);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function RichiestePage() {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [confirmation, setConfirmation] = useState<{ id: string; nome: string; cognome: string; telefono: string; email?: string; dataRitiro: string; items: { slug: string; name: string }[] } | null>(null);
  const items = useBooking((s) => s.items);
  const remove = useBooking((s) => s.remove);
  const clear = useBooking((s) => s.clear);
  const add = useBooking((s) => s.add);

  const { data: productsData } = useQuery({
    queryKey: ["public-products-for-quickadd"],
    queryFn: () => listPublicProducts(),
  });
  const productsHidden = useProductsHidden();

  const quickAdd = useMemo(() => {
    if (productsHidden) return [];
    const all: Product[] = productsData?.products ?? [];
    const cartSlugs = new Set(items.map((i) => i.slug));
    const bookable = all.filter((p) => p.bookable !== false && !cartSlugs.has(p.slug));
    const featured = bookable.filter((p) => p.featured);
    const rest = bookable.filter((p) => !p.featured);
    return [...featured, ...rest].slice(0, 4);
  }, [productsData, items, productsHidden]);

  // Form state
  const [nome, setNome] = useState("");
  const [cognome, setCognome] = useState("");
  const [telefono, setTelefono] = useState("");
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [dataRitiro, setDataRitiro] = useState(tomorrowIso());
  const [submitting, setSubmitting] = useState(false);
  const send = useServerFn(submitRichiesta);

  const canGoStep2 = items.length > 0;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!nome.trim() || !cognome.trim() || !telefono.trim() || !dataRitiro) {
      toast.error("Compila tutti i campi obbligatori");
      return;
    }
    if (dataRitiro < tomorrowIso()) {
      toast.error("La data di ritiro deve essere almeno domani");
      setDataRitiro(tomorrowIso());
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        nome: nome.trim(),
        cognome: cognome.trim(),
        telefono: telefono.trim(),
        email: email.trim() || undefined,
        note: note.trim() || undefined,
        dataRitiro,
        items: items.map((i) => ({ slug: i.slug, name: i.name })),
      };
      const res = await send({
        data: {
          ...payload,
        },
      });
      setConfirmation({
        id: res.id,
        nome: payload.nome,
        cognome: payload.cognome,
        telefono: payload.telefono,
        email: payload.email,
        dataRitiro: payload.dataRitiro,
        items: payload.items,
      });
      toast.success("Richiesta inviata!", {
        description: `ID richiesta: ${String(res.id).slice(0, 8).toUpperCase()}`,
      });
      clear();
      setStep(3);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Errore nell'invio della richiesta");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="container mx-auto px-4 pt-10 md:pt-14 pb-16 max-w-4xl">
          <Reveal y={16}>
            <div className="text-[11px] uppercase tracking-[0.22em] text-brand-red font-medium">
              {step === 3 ? "Richiesta inviata" : "Richiedi disponibilità"}
            </div>
            <h1 className="font-display text-3xl md:text-5xl text-brand-charcoal mt-2">
              {step === 1 && "Il tuo carrello"}
              {step === 2 && "I tuoi dati"}
              {step === 3 && "Grazie!"}
            </h1>
            <p className="mt-3 text-brand-warm-gray max-w-2xl">
              {step === 1 &&
                "Aggiungi i prodotti che ti interessano. Non è un ordine definitivo: verifichiamo la disponibilità e ti ricontattiamo per confermare data e ora di ritiro."}
              {step === 2 &&
                "Lasciaci i tuoi dati e la data desiderata (almeno domani). Ti ricontatteremo per confermare."}
            </p>
            {step !== 3 && <InfoRitiro className="mt-3 max-w-2xl" />}
          </Reveal>

          {/* Stepper */}
          <div className="mt-8 flex items-center gap-2 sm:gap-3 text-[10px] sm:text-xs uppercase tracking-[0.14em] sm:tracking-[0.16em]">
            <StepDot active={step === 1} done={step > 1} n={1} label="Carrello" />
            <div className="h-px flex-1 bg-brand-charcoal/15" />
            <StepDot active={step === 2} done={step > 2} n={2} label="Dati" />
            <div className="h-px flex-1 bg-brand-charcoal/15" />
            <StepDot active={step === 3} done={false} n={3} label="Invio" />
          </div>

          <div className="mt-8">
            {step === 1 && (
              <CartStep
                items={items}
                remove={remove}
                quickAdd={quickAdd}
                addQuick={(p) =>
                  add({ slug: p.slug, name: p.name, price: p.onOffer && p.offerPrice ? p.offerPrice : p.price, unit: p.unit, image: p.image })
                }
                canProceed={canGoStep2}
                onProceed={() => {
                  setStep(2);
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
              />
            )}

            {step === 2 && (
              <form onSubmit={onSubmit} className="grid md:grid-cols-[1fr,320px] gap-8">
                <div className="space-y-5 bg-white rounded-md p-5 md:p-7 shadow-[var(--shadow-soft)]">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Nome *" required>
                      <Input value={nome} onChange={(e) => setNome(e.target.value)} maxLength={80} required />
                    </Field>
                    <Field label="Cognome *" required>
                      <Input value={cognome} onChange={(e) => setCognome(e.target.value)} maxLength={80} required />
                    </Field>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Telefono *" required>
                      <Input type="tel" value={telefono} onChange={(e) => setTelefono(e.target.value)} maxLength={30} required />
                    </Field>
                    <Field label="Email (opzionale)">
                      <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} maxLength={200} />
                    </Field>
                  </div>
                  <Field label="Data desiderata per il ritiro *" required>
                    <Input
                      type="date"
                      value={dataRitiro}
                      min={tomorrowIso()}
                      onChange={(e) => {
                        const v = e.target.value;
                        const min = tomorrowIso();
                        setDataRitiro(v && v < min ? min : v);
                      }}
                      required
                    />
                    <p className="mt-2 text-xs font-medium text-brand-red">
                      ⚠️ Il ritiro è disponibile solo dal giorno successivo. Non è possibile richiedere per oggi.
                    </p>
                  </Field>
                  <Field label="Note (opzionale)">
                    <Textarea
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      maxLength={1000}
                      rows={4}
                      placeholder="Es. tagli preferiti, spessore delle fettine, porzioni…"
                    />
                  </Field>

                  <p className="text-xs text-brand-warm-gray leading-relaxed">
                    Inviando la richiesta dichiari di aver letto l'<Link to="/privacy" className="text-brand-red underline underline-offset-2">informativa privacy</Link>. Useremo i tuoi dati solo per verificare la disponibilità e ricontattarti.
                  </p>
                  <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-between gap-3 pt-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setStep(1)}
                      className="gap-2"
                    >
                      <ArrowLeft size={16} /> Indietro
                    </Button>
                    <Button
                      type="submit"
                      disabled={submitting}
                      className="gap-2 bg-brand-red hover:bg-brand-red-hover text-white text-xs uppercase tracking-[0.16em] px-6 py-6"
                    >
                      {submitting ? "Invio…" : "Invia richiesta"} <ArrowRight size={16} />
                    </Button>
                  </div>
                </div>

                <aside className="bg-brand-cream-dark/40 rounded-md p-5 h-fit sticky top-24">
                  <div className="text-[11px] uppercase tracking-[0.18em] text-brand-warm-gray mb-3">Riepilogo</div>
                  <ul className="space-y-2 text-sm">
                    {items.map((i) => (
                      <li key={i.slug} className="flex gap-2 text-brand-charcoal">
                        <span className="text-brand-red">•</span>
                        <span>{i.name}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="mt-4 pt-4 border-t border-brand-charcoal/10 text-[11px] text-brand-warm-gray leading-relaxed">
                    Ti ricontattiamo per confermare disponibilità, quantità e prezzo definitivo.
                  </p>
                </aside>
              </form>
            )}

            {step === 3 && <SuccessStep confirmation={confirmation} />}
          </div>
    </section>
  );
}

// Informazioni prima della richiesta (Reg. UE 1169/2011 art. 14: allergeni e, per le carni, origine
// prima dell'acquisto). Testo generico: i dati dei singoli prodotti li ha la bottega.
function InfoRitiro({ className = "" }: { className?: string }) {
  return (
    <p className={`text-xs text-brand-warm-gray leading-relaxed ${className}`}>
      Sul sito non si paga nulla e non ci sono spedizioni: si ritira in bottega, in Via J. F. Kennedy 1 a
      Campobasso. Allergeni e origine delle carni te li indichiamo quando ti richiamiamo per confermare e
      sono sempre disponibili al banco: se hai allergie o intolleranze, chiedicelo prima del ritiro.
    </p>
  );
}

function StepDot({ active, done, n, label }: { active: boolean; done: boolean; n: number; label: string }) {
  return (
    <div className="flex items-center gap-2">
      <div
        className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold ${
          done ? "bg-brand-green text-white" : active ? "bg-brand-red text-white" : "bg-brand-charcoal/10 text-brand-charcoal/60"
        }`}
      >
        {done ? "✓" : n}
      </div>
      <span className={active || done ? "text-brand-charcoal" : "text-brand-warm-gray"}>{label}</span>
    </div>
  );
}

function Field({ label, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-[0.14em] text-brand-charcoal/70 mb-1.5 block">{label}</Label>
      {children}
    </div>
  );
}

function CartStep({
  items,
  remove,
  quickAdd,
  addQuick,
  canProceed,
  onProceed,
}: {
  items: ReturnType<typeof useBooking.getState>["items"];
  remove: (slug: string) => void;
  quickAdd: Product[];
  addQuick: (p: Product) => void;
  canProceed: boolean;
  onProceed: () => void;
}) {
  const productsHidden = useProductsHidden();
  return (
    <div className="space-y-8">
      {items.length === 0 ? (
        <div className="bg-white rounded-md p-10 text-center shadow-[var(--shadow-soft)]">
          <ShoppingBag size={40} className="mx-auto text-brand-warm-gray/60 mb-4" />
          <p className="text-brand-warm-gray">Il tuo carrello è vuoto. Aggiungi qualche prodotto qui sotto o dalla pagina prodotti.</p>
          {!productsHidden && (
            <Link
              to="/prodotti"
              className="mt-5 inline-flex items-center gap-2 text-brand-red text-sm uppercase tracking-[0.16em] hover:gap-3 transition-all"
            >
              Sfoglia i prodotti <ArrowRight size={14} />
            </Link>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-md shadow-[var(--shadow-soft)] divide-y divide-brand-charcoal/10">
          {items.map((i) => (
            <div key={i.slug} className="p-4 sm:p-5 flex items-center gap-4">
              <img src={i.image} alt="" className="w-16 h-16 sm:w-20 sm:h-20 object-cover rounded-md bg-brand-cream-dark/40 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-display text-lg text-brand-charcoal truncate">{i.name}</div>
                <div className="text-xs text-brand-warm-gray mt-0.5">Verificheremo disponibilità e quantità</div>
              </div>
              <button
                type="button"
                onClick={() => remove(i.slug)}
                className="text-brand-warm-gray hover:text-brand-red p-2"
                aria-label="Rimuovi"
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
          <div className="p-4 sm:p-5 bg-brand-cream-dark/30 text-xs text-brand-warm-gray leading-relaxed">
            Questa è una richiesta di disponibilità, non un ordine. Ti ricontattiamo per confermare quantità e prezzo.
          </div>
        </div>
      )}

      {/* Quick add */}
      {quickAdd.length > 0 && (
        <div>
          <div className="flex justify-center mb-6" aria-hidden>
            <div className="w-10 h-10 rounded-full border border-brand-red/30 flex items-center justify-center text-brand-red animate-bounce">
              <ArrowDown size={18} />
            </div>
          </div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-brand-red font-medium">Aggiungi rapidamente</div>
          <h2 className="font-display text-2xl text-brand-charcoal mt-1.5">Altri prodotti che potrebbero interessarti</h2>
          <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {quickAdd.map((p) => (
              <button
                key={p.slug}
                type="button"
                onClick={() => addQuick(p)}
                className="group text-left bg-white rounded-md overflow-hidden shadow-[var(--shadow-soft)] hover:shadow-lg transition-shadow"
              >
                <div className="aspect-square bg-brand-cream-dark/40 overflow-hidden">
                  {p.image ? (
                    <img src={p.image} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  ) : null}
                </div>
                <div className="p-3">
                  <div className="font-display text-sm text-brand-charcoal line-clamp-2 min-h-[2.5rem]">{p.name}</div>
                  <div className="mt-2 flex items-center justify-end">
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] text-brand-red group-hover:gap-1.5 transition-all">
                      <Plus size={12} /> Aggiungi
                    </span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex justify-end pt-2">
        <Button
          type="button"
          onClick={onProceed}
          disabled={!canProceed}
          className="gap-2 bg-brand-red hover:bg-brand-red-hover text-white text-xs uppercase tracking-[0.16em] px-6 py-6 disabled:opacity-40"
        >
          Vai avanti <ArrowRight size={16} />
        </Button>
      </div>
    </div>
  );
}

function SuccessStep({ confirmation }: { confirmation: { id: string; nome: string; cognome: string; telefono: string; email?: string; dataRitiro: string; items: { slug: string; name: string }[] } | null }) {
  const productsHidden = useProductsHidden();
  const shortId = confirmation ? String(confirmation.id).slice(0, 8).toUpperCase() : "";
  const dataFmt = confirmation
    ? new Date(confirmation.dataRitiro + "T12:00:00").toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long", year: "numeric" })
    : "";
  return (
    <div className="bg-white rounded-md p-8 md:p-12 shadow-[var(--shadow-soft)] text-center">
      <div className="w-16 h-16 rounded-full bg-brand-green/10 flex items-center justify-center mx-auto">
        <CheckCircle2 size={40} className="text-brand-green" />
      </div>
      <h2 className="mt-5 font-display text-3xl text-brand-charcoal">Richiesta inviata!</h2>
      <p className="mt-3 text-brand-warm-gray max-w-md mx-auto">
        Grazie. Abbiamo ricevuto la tua richiesta di disponibilità. Ti
        ricontatteremo il prima possibile al numero che ci hai lasciato per
        confermare i prodotti e l'orario di ritiro.
      </p>
      {confirmation && (
        <div className="mt-6 mx-auto max-w-md text-left bg-brand-cream-dark/30 border border-brand-charcoal/10 rounded-md p-5">
          <div className="flex items-center justify-between gap-3 pb-3 border-b border-brand-charcoal/10">
            <span className="text-[11px] uppercase tracking-[0.18em] text-brand-warm-gray">ID richiesta</span>
            <span className="font-mono text-sm text-brand-charcoal">#{shortId}</span>
          </div>
          <dl className="mt-3 space-y-1.5 text-sm text-brand-charcoal">
            <div className="flex gap-2"><dt className="text-brand-warm-gray min-w-[90px]">Nome</dt><dd>{confirmation.nome} {confirmation.cognome}</dd></div>
            <div className="flex gap-2"><dt className="text-brand-warm-gray min-w-[90px]">Telefono</dt><dd>{confirmation.telefono}</dd></div>
            {confirmation.email && <div className="flex gap-2"><dt className="text-brand-warm-gray min-w-[90px]">Email</dt><dd>{confirmation.email}</dd></div>}
            <div className="flex gap-2"><dt className="text-brand-warm-gray min-w-[90px]">Ritiro</dt><dd className="capitalize">{dataFmt}</dd></div>
          </dl>
          <div className="mt-3 pt-3 border-t border-brand-charcoal/10">
            <div className="text-[11px] uppercase tracking-[0.18em] text-brand-warm-gray mb-1.5">Prodotti</div>
            <ul className="text-sm text-brand-charcoal space-y-0.5">
              {confirmation.items.map((i) => (
                <li key={i.slug} className="flex gap-2"><span className="text-brand-red">•</span><span>{i.name}</span></li>
              ))}
            </ul>
          </div>
        </div>
      )}
      <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
        {!productsHidden && (
          <Link
            to="/prodotti"
            className="inline-flex items-center gap-2 bg-brand-red hover:bg-brand-red-hover text-white px-6 py-3 rounded-md text-xs uppercase tracking-[0.16em]"
          >
            <ShoppingBag size={14} /> Torna ai prodotti
          </Link>
        )}
        <a
          href="tel:+393407487310"
          className="inline-flex items-center gap-2 border border-brand-charcoal/25 hover:border-brand-red hover:text-brand-red text-brand-charcoal px-6 py-3 rounded-md text-xs uppercase tracking-[0.16em]"
        >
          <Phone size={14} /> Chiamaci ora
        </a>
      </div>
    </div>
  );
}